import { createIndex } from "../../../../shared/utils/array";
import { getObjectEntries } from "../../../../shared/utils/objects";
import { DefaultRolePermissions } from "../../defaults/defaults";
import { FeaturePermissionsTemplate, getAppPermissionsTemplate, getFeaturePermissionsTypeDefaults } from "./templates/templates";
import { FeaturePermissions, FeaturePermissionsIndex, HoldingUserPermissions, UserPermissions } from "./types";

export function convertFromHoldingPermissions(permissions: HoldingUserPermissions, memberPermissions: UserPermissions, ownerPermissions: UserPermissions, defaultPermissions: DefaultRolePermissions): FeaturePermissionsIndex {

    const flat = [
        ...convertAppPermissions("hub"),
        ...convertAppPermissions("scout")
    ]

    const index = createIndex(flat, f => f.key)

    return index

    function convertAppPermissions(app: keyof HoldingUserPermissions): FeaturePermissions[] {

        const appPermissions = permissions?.[app]
        const appTemplate = getAppPermissionsTemplate(app)

        return getObjectEntries(appTemplate.permissions)
            .map(e => {

                const feature = e[0]
                const template = e[1] as FeaturePermissionsTemplate<{}>
                const defaultValues = getFeaturePermissionsTypeDefaults(template.type)

                const values = appPermissions
                    ? template.selector(appPermissions)
                    : { ...defaultValues }

                const fp: FeaturePermissions = {

                    app,
                    feature,
                    template,
                    defaultValues,
                    key: `${app}.${feature}`,
                    type: template.type,

                    updated: false,

                    availability: {
                        owner: template.isAvailable(ownerPermissions, defaultPermissions),
                        member: template.isAvailable(memberPermissions, defaultPermissions)
                    },

                    values,
                    initialValues: {...values}
                }

                fp.values = normalizePermissions(fp, fp.values)

                return fp

            })

    }

}

export function convertToHoldingPermissions(features: FeaturePermissionsIndex): HoldingUserPermissions {

    const target: HoldingUserPermissions = {
        hub: {} as any,
        scout: {} as any
    }

    /** Top level properties */
    for (const f of Object.values(features).filter(f => !f.template.nestedProperty)) {
        target[f.app][f.feature] = f.values
    }

    /** Nested properties */
    for (const f of Object.values(features).filter(f => !!f.template.nestedProperty)) {
        f.template.nestedProperty.setter(target, f.values)
    }

    return target

}

export function applyUpdatesToPermissions(target: FeaturePermissionsIndex, updates: { key: string, values: {} }[]): FeaturePermissionsIndex {

    target = {
        ...target
    }

    for (const u of updates) {

        const t = target[u.key]

        if (t) {

            const values = normalizePermissions(t, u.values)

            target[u.key] = {
                ...t,
                values,
                updated: !compareValues(t.initialValues, values)
            }

        }

    }

    return target

}


export function compareHoldingPermissions(current: FeaturePermissionsIndex, initial: FeaturePermissionsIndex) {

    function compareFeature(feature: string): boolean {

        const c = current[feature]
        const cv = c.values ?? c.defaultValues

        const i = initial[feature]
        const iv = i.values ?? i.defaultValues

        return compareValues(cv, iv)

    }

    return Object.keys(initial).every(key => compareFeature(key))

}

function compareValues(a: {}, b: {}) {
    return Object.keys(a).every(key => a[key] == b[key])
}

function normalizePermissions(featurePermissions: FeaturePermissions, values: {}) {

    const { defaultValues } = featurePermissions

    if (!values) {
        return { ...defaultValues }
    }

    // If the template has a "read" property, and this property is currently false,
    // then all properties are set to false
    // Otherwise, they are simply normalised as booleans (e.g., to replace undefined/null by false)
    const transform: (a: boolean) => boolean = (defaultValues.hasOwnProperty("read") && Boolean(values["read"]) === false)
        ? _ => false
        : v => Boolean(v)

    values = Object.fromEntries(
        Object.keys(defaultValues).map(key => [key, transform(values[key])])
    )

    return values

}